import React, { useEffect, useState } from 'react';
import { Sparkles, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { BRIDGE_ASSISTANT_INTRO_KEY } from '../../content/bridgeAssistantContent';

export function BridgeAssistantLauncher({ open, onToggle, hasMobileBottomNav }) {
  const [showIntro, setShowIntro] = useState(false);
  const [pulseOnce, setPulseOnce] = useState(true);

  useEffect(() => {
    try {
      const dismissed = localStorage.getItem(BRIDGE_ASSISTANT_INTRO_KEY) === '1';
      setShowIntro(!dismissed && !open);
    } catch {
      setShowIntro(false);
    }
  }, [open]);

  useEffect(() => {
    if (!pulseOnce) return undefined;
    const t = window.setTimeout(() => setPulseOnce(false), 4000);
    return () => window.clearTimeout(t);
  }, [pulseOnce]);

  const dismissIntro = (e) => {
    e?.stopPropagation?.();
    try {
      localStorage.setItem(BRIDGE_ASSISTANT_INTRO_KEY, '1');
    } catch {
      /* ignore */
    }
    setShowIntro(false);
  };

  const openFromIntro = () => {
    dismissIntro();
    onToggle(true);
  };

  const positionClass = hasMobileBottomNav
    ? 'bottom-[calc(4.5rem+env(safe-area-inset-bottom,0px))] right-3 sm:bottom-[4.75rem] sm:right-4'
    : 'bottom-[calc(1.25rem+env(safe-area-inset-bottom,0px))] right-4 sm:bottom-6 sm:right-6';

  return (
    <div
      className={cn('fixed z-[54] flex flex-col items-end gap-2', positionClass)}
      data-testid="bridge-assistant-launcher"
    >
      {showIntro && !open ? (
        <div
          className={cn(
            'relative max-w-[15rem] rounded-xl border border-[hsl(38_14%_86%)] bg-[hsl(42_28%_98%)] px-3 py-2.5',
            'text-xs leading-relaxed text-[hsl(24_14%_18%)] shadow-lg',
            'animate-in fade-in-0 slide-in-from-bottom-2 duration-300',
          )}
          role="status"
        >
          <button
            type="button"
            className="absolute right-1.5 top-1.5 rounded-full p-0.5 text-muted-foreground hover:bg-muted/60"
            onClick={dismissIntro}
            aria-label="Dismiss tip"
          >
            <X className="h-3 w-3" />
          </button>
          <p className="pr-5">Need help finding a feature?</p>
          <button
            type="button"
            className="mt-1.5 font-medium text-[hsl(152_28%_22%)] hover:underline"
            onClick={openFromIntro}
          >
            Open Bridge Assistant
          </button>
        </div>
      ) : null}

      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={() => onToggle(!open)}
            className={cn(
              'flex h-14 w-14 items-center justify-center rounded-full',
              'border border-[#c9a961]/35 bg-[hsl(152_28%_18%)] text-[#faf7f2]',
              'shadow-[0_8px_32px_-8px_hsl(152_28%_12%_/_0.55)] transition-transform hover:scale-[1.03] hover:bg-[hsl(152_28%_16%)]',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c9a961]/50 focus-visible:ring-offset-2',
              open && 'ring-2 ring-[#c9a961]/45 ring-offset-2 ring-offset-[hsl(42_28%_96%)]',
              pulseOnce && !open && 'animate-[bridge-assistant-pulse_2.5s_ease-in-out_1]',
            )}
            aria-label={open ? 'Close Bridge Assistant' : 'Ask Bridge Assistant'}
            aria-expanded={open}
            data-testid="bridge-assistant-fab"
          >
            <Sparkles className="h-5 w-5" strokeWidth={1.75} aria-hidden />
          </button>
        </TooltipTrigger>
        <TooltipContent side="left" className="text-xs font-medium">
          Ask Bridge Assistant
        </TooltipContent>
      </Tooltip>
    </div>
  );
}

export default BridgeAssistantLauncher;
